import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../firebase/AuthContext";
import { useAnalytics } from "../../firebase/hooks/useAnalytics";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import TabButton from "./TabButton";
import { getApiUrl } from "../../config/api";

// Import panel components
import OverviewPanel from "./panels/OverviewPanel";
import UsersPanel from "./panels/UsersPanel";
import RolesPanel from "./panels/RolesPanel";
import GroupsPanel from "./panels/GroupsPanel";
import AuditPanel from "./panels/AuditPanel";
import UserSyncPanel from "./panels/UserSyncPanel";
import UsageBillingPanel from "./panels/UsageBillingPanel";

export default function Admin() {
  const { currentUser, hasRole, claims } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPageView } = useAnalytics();

  // Real data state (replacing mock data)
  const [users, setUsers] = useState([]);
  const [dynamicRoles, setDynamicRoles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [audits, setAudits] = useState([]);
  const [stats] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Pagination state for users
  const [pagination, setPagination] = useState({
    pageSize: 10,
    nextCursor: null,
    prevCursor: null,
    hasNext: false,
    hasPrev: false,
  }); // Separate loading state for pagination operations
  const [usersLoading, setUsersLoading] = useState(false);

  // Modal state for navigation confirmation
  const [showExitModal, setShowExitModal] = useState(false);

  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };

  const [language, setLanguage] = useState(getLangFromUrl());

  // Track page view when component mounts
  useEffect(() => {
    trackPageView("Admin Panel");
  }, [trackPageView]);

  useEffect(() => {
    setLanguage(getLangFromUrl());
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    if (!currentUser) {
      navigate({ pathname: "/login", search: `?lang=${language}` });
    }
  }, [currentUser, navigate, language]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line
  }, [language]);

  const loadDynamicRoles = useCallback(async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${getApiUrl()}/api/admin/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success && data.roles) {
        setDynamicRoles(data.roles);
        // console.log(data.roles);
      }
    } catch (error) {
      console.error("Failed to load dynamic roles:", error);
    }
  }, [currentUser]);

  // limit = int(request.args.get("limit", 10))
  //     page_token = request.args.get("pageToken")  # Cursor for pagination
  //     direction = request.args.get("direction", "next")  # Cursor for pagination
  //     search_query = request.args.get("search", "").strip() or None

  const loadUsers = useCallback(
    async (
      pageSize = 10,
      startAfter = null,
      searchQuery = "",
      direction = "next",
    ) => {
      setUsersLoading(true);

      // console.log(startAfter, direction);
      try {
        const token = await currentUser.getIdToken();

        const params = new URLSearchParams();
        params.append("limit", pageSize.toString());
        params.append("direction", direction);

        if (startAfter) {
          params.append("pageToken", startAfter);
        }

        if (searchQuery && searchQuery.trim()) {
          params.append("search", searchQuery.trim());
        }

        const response = await fetch(
          `${getApiUrl()}/api/admin/users?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json();

        // console.log(data);

        if (data.users) {
          setUsers(data.users || []);

          setPagination((prev) => ({
            pageSize: pageSize,
            nextCursor: data.nexttoken,
            prevCursor: data.prevCursor,
            hasNext: data.hasNext || false,
            hasPrev: data.hasPrev || false,
          }));
        } else {
          console.error("Failed to load users:", data.error);
          setUsers([]);
          setPagination((prev) => ({
            ...prev,
            nextCursor: null,
            prevCursor: null,
            hasNext: false,
            hasPrev: false,
          }));
        }
      } catch (error) {
        console.error("Failed to load users:", error);
        setUsers([]);
        setPagination((prev) => ({
          ...prev,
          nextCursor: null,
          prevCursor: null,
          hasNext: false,
          hasPrev: false,
        }));
      } finally {
        setUsersLoading(false);
      }
    },
    [currentUser],
  );

  const loadGroups = useCallback(async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${getApiUrl()}/api/admin/groups`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success && data.groups) {
        setGroups(data.groups);
      }
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  }, [currentUser]);

  const loadAuditLogs = useCallback(async () => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${getApiUrl()}/api/admin/audit-logs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success && data.audit_logs) {
        setAudits(
          data.audit_logs.map((log) => ({
            ...log,
            user: log.admin_email,
            actor: log.admin_email,
            ipAddress: log.ip_address,
          })),
        );
      }
    } catch (error) {
      console.error("Failed to load audit logs:", error);
    }
  }, [currentUser]);

  const loadAllData = useCallback(async () => {
    setLoadingData(true);
    try {
      await Promise.all([
        loadUsers(), // Default page size
        loadGroups(),
        loadAuditLogs(),
        // loadSystemStats(),
        loadDynamicRoles(),
      ]);
    } catch (error) {
      console.error("Failed to load admin data:", error);
    } finally {
      setLoadingData(false);
    }
  }, [loadUsers, loadGroups, loadAuditLogs, loadDynamicRoles]);

  useEffect(() => {
    // && hasRole("admin")
    if (currentUser) {
      loadAllData();
    }
  }, [currentUser, hasRole, loadAllData]);

  // Moves one user into a group (the Users tab passes a uid and gets an
  // alert), or several at once (the Groups tab passes a list of uids and gets
  // back { moved, failed: [{ userId, error }] } to report who didn't move).
  // "Removing" someone from a group is moving them to "user".
  const updateUserGroups = async (uids, groups) => {
    const bulk = Array.isArray(uids);
    const failAll = (error) => ({
      moved: [],
      failed: uids.map((userId) => ({ userId, error })),
    });
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/admin/assign-user-group`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(
            bulk
              ? { userIds: uids, groupId: groups }
              : { userId: uids, groupId: groups },
          ),
        },
      );

      const data = await response.json();
      if (bulk) {
        return data.moved
          ? { moved: data.moved, failed: data.failed || [] }
          : failAll(data.error || `Request failed (${response.status})`);
      }
      if (data.success) {
        // Reload users to see changes with current pagination
        await loadUsers(pagination.pageSize, null, "reset");
        alert("Groups updated successfully");
      } else {
        alert(`Failed to update groups: ${data.error}`);
      }
    } catch (error) {
      console.error("Failed to update groups:", error);
      if (bulk) return failAll(error.message);
      alert("Failed to update groups");
    } finally {
      await loadAllData();
    }
  };

  const createGroup = async (groupData) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${getApiUrl()}/api/admin/create-group`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(groupData),
      });

      const data = await response.json();
      if (data.success) {
        // Reload groups to see changes
        await loadGroups();
        alert("Group created successfully");
        return true;
      } else {
        alert(`Failed to create group: ${data.error}`);
        return false;
      }
    } catch (error) {
      console.error("Failed to create group:", error);
      alert("Failed to create group");
      return false;
    }
  };

  const fetchGroupDetails = async (groupId, page = 1, pageSize = 10) => {
    try {
      const token = await currentUser.getIdToken();
      const params = new URLSearchParams();
      params.append("groupId", groupId);
      params.append("page", page.toString());
      params.append("pageSize", pageSize.toString());

      const response = await fetch(
        `${getApiUrl()}/api/admin/group-details?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      // console.log(data);
      if (data.success && data.group) {
        return data.group;
      } else {
        alert(`Failed to fetch group details: ${data.error}`);
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      alert("Failed to fetch group details");
      return null;
    }
  };

  const updateGroupRoles = async (groupId, roles) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/admin/add-role-to-group`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ groupId, roles }),
        },
      );

      const data = await response.json();
      if (data.success) {
        // Reload users to see changes
        await loadUsers();
        alert("Groups updated successfully");
        return true;
      }
      alert(`Failed to update groups: ${data.error}`);
      return false;
    } catch (error) {
      console.error("Failed to update group roles:", error);
      alert("Failed to update group roles");
      return false;
    } finally {
      await loadAllData();
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${getApiUrl()}/api/admin/delete-group`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ groupId }),
      });

      const data = await response.json();
      if (data.success) {
        // Reload groups to see changes
        await loadGroups();
        alert("Group deleted successfully");
        return true;
      } else {
        alert(`Failed to delete group: ${data.error}`);
        return false;
      }
    } catch (error) {
      console.error("Failed to delete group:", error);
      alert("Failed to delete group");
      return false;
    }
  };

  // Modal handlers for navigation confirmation
  const handleBackToDashboard = () => {
    setShowExitModal(true);
  };

  const confirmNavigation = () => {
    setShowExitModal(false);
    navigate({
      pathname: "/tg-product-assistant",
      search: `?lang=${language}`,
    });
  };

  const cancelNavigation = () => {
    setShowExitModal(false);
  };

  const currentUserRole = {
    role: claims.groupId || "user",
    // permissions: claims.permissions || [],
    // groups: claims.groups || [],
  };

  // if (!loading && !hasRole("admin")) {
  //     return (
  //         <div className="flex h-screen bg-dashboard-bg items-center justify-center">
  //             <div className="text-white text-lg">
  //                 Access Denied - Admin role required
  //             </div>
  //         </div>
  //     );
  // }

  // --- UI state for admin panels ---

  // Enhanced Loading state for UI
  if (loadingData) {
    return (
      <div className="flex h-screen bg-dashboard-bg text-white font-sans">
        <LanguageSwitcher language={language} setLanguage={setLanguage} />
        <div className="flex-1 flex flex-col gap-8 items-center justify-center -mt-36">
          <div className="flex flex-col gap-6 items-center text-center">
            {/* Enhanced Loading Animation */}
            {/* <div className="relative">
                            <div className="w-20 h-20 border-4 border-white/20 rounded-full"></div>
                            <div className="absolute top-0 left-0 w-20 h-20 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                            <div
                                className="absolute top-2 left-2 w-16 h-16 border-4 border-blue-400/40 border-t-transparent rounded-full animate-spin"
                                style={{
                                    animationDirection: "reverse",
                                    animationDuration: "1.5s",
                                }}
                            ></div>
                        </div> */}

            {/* Enhanced Loading Text */}
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-white">
                {language === "zh" ? "正在加载管理面板" : "Loading Admin Panel"}
              </h2>
              <div className="flex items-center gap-3 justify-center">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
                <span className="text-orange-300 text-lg font-medium">
                  {language === "zh" ? "正在準備數據..." : "Preparing data..."}
                </span>
              </div>
            </div>

            {/* Enhanced Description */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 max-w-md">
              <p className="text-slate-100 text-sm leading-relaxed">
                {language === "zh"
                  ? "請稍候，我們正在為您加載用戶、組織和權限數據。這可能需要幾秒鐘的時間。"
                  : "Please wait while we load users, groups, and permissions data. This may take a few seconds."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-dashboard-bg text-black font-sans">
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="flex-1 flex flex-col min-h-screen overflow-auto">
        {/* Enhanced Current User Role Info */}
        <div className="w-full justify-self-center bg-gradient from-gray-900 via-gray-800 to-gray-700 p-4 sm:p-6 lg:p-8 text-white">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start sm:items-center justify-between pt-12 sm:pt-6 lg:pt-8">
            <div className="flex flex-col space-y-4 sm:space-y-6">
              {/* Enhanced Back Button */}

              {/* Enhanced Role Section */}
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg sm:text-xl text-white">
                      {language === "en"
                        ? "Your Role & Permissions"
                        : "您的角色和權限"}
                    </h3>
                    <p className="text-blue-200 text-xs sm:text-sm">
                      {language === "en"
                        ? "Administrative access panel"
                        : "管理訪問面板"}
                    </p>
                  </div>
                </div>

                {/* Enhanced Role Badge */}
                <div className="inline-flex items-center gap-2 sm:gap-3 px-4 py-2 sm:px-6 sm:py-3 bg-orange-500 rounded-xl shadow-lg backdrop-blur-sm border border-orange-400/30">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-white/20 rounded-full flex items-center justify-center">
                    <svg
                      className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-xs sm:text-sm">
                      {language === "en" ? "Current Role" : "當前角色"}
                    </p>
                    <p className="text-orange-100 font-bold capitalize text-sm sm:text-base">
                      {currentUserRole.role}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Action Button */}
            <div className="flex gap-2 sm:gap-3 self-start sm:self-end md:mt-4 mt-2">
              <div
                onClick={handleBackToDashboard}
                className="flex p-2 sm:p-3 flex-row gap-2 sm:gap-3 items-center rounded-xl bg-white/10 hover:bg-white/20 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer w-fit backdrop-blur-sm border border-white/20"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-orange-500 flex items-center justify-center shadow-lg">
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="text-white font-semibold text-xs sm:text-sm">
                  {language === "en" ? "Back to Home" : "首頁"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-white w-full justify-self-center shadow-sm">
          {/* Tabs - Modern Card Style */}
          <div className="flex justify-center px-3 sm:px-6 py-4 sm:py-6">
            <div className="inline-flex gap-1 sm:gap-2 p-1 sm:p-1.5 bg-gray-100 rounded-xl w-full max-w-full overflow-x-auto">
              <TabButton
                label={language === "en" ? "Overview" : "概覽"}
                active={activeTab === "overview"}
                onClick={() => setActiveTab("overview")}
              />
              {hasRole("users.read") && (
                <TabButton
                  label={language === "en" ? "Users" : "用戶"}
                  active={activeTab === "users"}
                  onClick={() => setActiveTab("users")}
                />
              )}
              {hasRole("groups.read") && (
                <TabButton
                  label={language === "en" ? "Roles" : "角色"}
                  active={activeTab === "roles"}
                  onClick={() => setActiveTab("roles")}
                />
              )}
              {hasRole("groups.read") && (
                <TabButton
                  label={language === "en" ? "Groups" : "群組"}
                  active={activeTab === "groups"}
                  onClick={() => setActiveTab("groups")}
                />
              )}
              {hasRole("billing.view_all") && (
                <TabButton
                  label={language === "en" ? "Usage & Billing" : "用量與計費"}
                  active={activeTab === "usage"}
                  onClick={() => setActiveTab("usage")}
                />
              )}
              {hasRole("audit.read") && (
                <TabButton
                  label={language === "en" ? "Audit Logs" : "審計日誌"}
                  active={activeTab === "audits"}
                  onClick={() => setActiveTab("audits")}
                />
              )}
              {hasRole("users.sync") && (
                <TabButton
                  label={language === "en" ? "Sync" : "同步"}
                  active={activeTab === "sync"}
                  onClick={() => setActiveTab("sync")}
                />
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col p-3 sm:p-4 lg:p-6 overflow-auto">
            {activeTab === "overview" && (
              <OverviewPanel
                setActiveTab={setActiveTab}
                roles={dynamicRoles}
                groups={groups}
                audits={audits}
                stats={stats}
                language={language}
              />
            )}

            {activeTab === "users" && (
              <UsersPanel
                users={users}
                hasRole={hasRole}
                groups={groups}
                language={language}
                onUpdateGroups={updateUserGroups}
                onLoadUsers={loadUsers}
                pagination={pagination}
                isLoading={usersLoading}
              />
            )}

            {activeTab === "roles" && (
              <RolesPanel roles={dynamicRoles} groups={groups} language={language} />
            )}

            {activeTab === "groups" && (
              <GroupsPanel
                hasRole={hasRole}
                groups={groups}
                roles={dynamicRoles}
                language={language}
                onCreateGroup={createGroup}
                fetchGroupDetails={fetchGroupDetails}
                updateGroupRoles={updateGroupRoles}
                onDeleteGroup={deleteGroup}
                onUpdateGroups={updateUserGroups}
              />
            )}

            {activeTab === "audits" && (
              <AuditPanel audits={audits} language={language} />
            )}

            {activeTab === "usage" && (
              <UsageBillingPanel
                language={language}
                currentUser={currentUser}
                hasRole={hasRole}
              />
            )}

            {activeTab === "sync" && (
              <UserSyncPanel
                language={language}
                currentUser={currentUser}
                onSyncComplete={loadAllData}
                hasRole={hasRole}
              />
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={cancelNavigation}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-amber-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {language === "en"
                      ? "Leave Admin Panel?"
                      : "離開管理面板？"}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === "en"
                      ? "Unsaved changes will be lost"
                      : "未保存的更改將丟失"}
                  </p>
                </div>
              </div>
              <button
                onClick={cancelNavigation}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-sm text-gray-700">
                {language === "en"
                  ? "Are you sure you want to leave the admin panel? Any unsaved changes will be lost."
                  : "您確定要離開管理面板嗎？任何未保存的更改都將丟失。"}
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={cancelNavigation}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                {language === "en" ? "Cancel" : "取消"}
              </button>
              <button
                onClick={confirmNavigation}
                className="px-5 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-xl shadow-sm transition-all"
              >
                {language === "en" ? "Leave Admin Panel" : "離開管理面板"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
